import { useRef, useEffect } from 'react';
import { useAudioPlayback } from '#/contexts/PlaybackContext';
import logger from "#/utils/logger";

interface AudioVisualizerProps {
  onVisualizerStop?: () => void;
}

const BARS_TO_DISPLAY = 9;
const BAR_PADDING = 5;
const BAR_GROUP_PADDING = 4;
const BAR_WIDTH = 3;
const HALF_BAR_WIDTH = Math.round(BAR_WIDTH / 2.0);
const CANVAS_WIDTH = 88;
const CANVAS_HEIGHT = 32;

const generateStaticPattern = (): Uint8Array => {
  const array = new Uint8Array(BARS_TO_DISPLAY);
  for (let i = 0; i < BARS_TO_DISPLAY; i++) {
    array[i] = 128 + Math.round(9 * Math.sin(i));
  }
  return array;
};

export const AudioVisualizer = ({ onVisualizerStop }: AudioVisualizerProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastFrequencyData = useRef<Uint8Array | null>(null);
  const { playbackObjects, isPlaying, isPaused } = useAudioPlayback();
  const isAnimatingRef = useRef(false);
  const lastFrameData = useRef<Uint8Array | null>(null);
  const lastFrameTimestamp = useRef<DOMHighResTimeStamp | null>(null)
  const smoothedHeights = useRef<number[]>(new Array(BARS_TO_DISPLAY).fill(0));

  const drawAudioBars = (
    canvasCtx: CanvasRenderingContext2D,
    array: Uint8Array,
    fillStyle: string | null = null,
    force = false
  ) => {
    if (!force && lastFrameData.current) {
      let totalDifference = 0;
      for (let i = 0; i < array.length; i++) {
        totalDifference += Math.abs(array[i] - lastFrameData.current[i]);
      }
      if (totalDifference < 10) return;
    }

    lastFrameData.current = new Uint8Array(array);

    let x = 0;
    for (let i = 0; i < BARS_TO_DISPLAY; i++) {
      if (i % 3 === 0 && i !== 0) {
        x += BAR_GROUP_PADDING;
      }

      const dataIndex = Math.round((i / BARS_TO_DISPLAY) * array.length);
      const sampleValue = array[dataIndex] || 128;
      const amplitude = Math.abs(sampleValue - 128) / 128;

      let scaledIntensity;
      if (amplitude < 0.01) scaledIntensity = amplitude * 2;
      else if (amplitude < 0.05) scaledIntensity = 0.02 + (amplitude - 0.01) * 5;
      else if (amplitude < 0.1) scaledIntensity = 0.22 + (amplitude - 0.05) * 8;
      else if (amplitude < 0.2) scaledIntensity = 0.62 + (amplitude - 0.1) * 3;
      else scaledIntensity = 0.92 + (amplitude - 0.2) * 0.1;

      scaledIntensity = Math.min(1.0, scaledIntensity * 2.5);
      const targetHeight = scaledIntensity * CANVAS_HEIGHT;
      smoothedHeights.current[i] += (targetHeight - smoothedHeights.current[i]) * 0.3;
      const barHeight = smoothedHeights.current[i];

      if (fillStyle) {
        canvasCtx.fillStyle = fillStyle;
      } else {
        canvasCtx.fillStyle = '#fff';
      }
      const centerY = CANVAS_HEIGHT / 2;
      canvasCtx.fillRect(
        x + HALF_BAR_WIDTH,
        centerY - barHeight / 2,
        BAR_WIDTH,
        barHeight
      );
      x += BAR_WIDTH + BAR_PADDING;
    }
  };

  const drawVisualBars = (
    canvasCtx: CanvasRenderingContext2D,
    timestamp: DOMHighResTimeStamp
  ) => {
    if (!lastFrameTimestamp.current || !timestamp) {
      // Initial draw
      lastFrameTimestamp.current = timestamp;
    }
    else {
      if (timestamp - lastFrameTimestamp.current < 50.0) {
        // Update at max 20FPS
        return;
      }
    }

    let x = 0;
    for (let i = 0; i < BARS_TO_DISPLAY; i++) {
      if (i % 4 === 0 && i !== 0) {
        x += BAR_GROUP_PADDING;
      }

      // Alternate bar frequencies (cycle through array)
      const frequency = 1.0 + (i % 4 - 1.5) * 0.5;
      const scaledIntensity = Math.abs(Math.random()
                                        * Math.sin(Math.PI * timestamp * frequency / 1000));

      const targetHeight = scaledIntensity * CANVAS_HEIGHT;
      smoothedHeights.current[i] = Math.max(targetHeight,
                                            (smoothedHeights.current[i] * 0.7 + targetHeight * 0.3));
      const barHeight = smoothedHeights.current[i];

      canvasCtx.fillStyle = '#fff';
      const centerY = CANVAS_HEIGHT / 2;
      canvasCtx.fillRect(
        x + HALF_BAR_WIDTH,
        centerY - barHeight / 2,
        BAR_WIDTH,
        barHeight
      );
      x += BAR_WIDTH + BAR_PADDING;
    }
  }

  const drawVisualizer = (): (() => void) => {
    if (playbackObjects.current) {
      const analyser = playbackObjects.current.analyser;
      const canvasCtx = canvasRef.current?.getContext('2d');
      if (!canvasCtx) return () => {};

      let frameCount = 0;
      const drawAudio = (timestamp: DOMHighResTimeStamp) => {
        if (isAnimatingRef.current) {
          requestAnimationFrame(drawAudio);
          frameCount++;
          if (frameCount % 5 !== 0) return;

          if (analyser) {
            const freqData = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteTimeDomainData(freqData);
            lastFrequencyData.current = freqData;
            canvasCtx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            drawAudioBars(canvasCtx, freqData);
          }
          else {
            canvasCtx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            drawVisualBars(canvasCtx, timestamp);
          }
        }
      };

      requestAnimationFrame(drawAudio);
      return () => {
        onVisualizerStop?.();
      };
    }
    return () => {};
  };

  // 1) Initial-mount placeholder (optional)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    if (playbackObjects.current) {
      if (playbackObjects.current.analyser) {
        logger.debug("Initializing analyser visualizer");
        const analyser = playbackObjects.current.analyser;
        const initData = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteTimeDomainData(initData);
        drawAudioBars(ctx, initData);
      }
      else {
        logger.debug("Initializing fake visualizer");
        drawVisualBars(ctx, 0);
      }
    }
  }, []);

  // 2) Play-loop effect: start/stop animation
  useEffect(() => {
    logger.debug("Updating start/stop animation with isPlaying=" + isPlaying);
    if (isPlaying) {
      isAnimatingRef.current = true;
      const stopAnimation = drawVisualizer();
      return () => {
        isAnimatingRef.current = false;
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        stopAnimation();
      };
    }
  }, [isPlaying]);

  // 3) Static-draw effect: draw static when truly stopped
  useEffect(() => {
    logger.debug("AudioVisualizer: isPlaying=", isPlaying, "isPaused=", isPaused);
    if (!isPlaying && !isPaused && !isAnimatingRef.current) {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn('No 2D context');
        return;
      }

      // Ensure previous frame is gone and smoothing restarts from zero
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      smoothedHeights.current = new Array(BARS_TO_DISPLAY).fill(0);
      lastFrameData.current = null;

      const data = lastFrequencyData.current;
      if (data && data.some(v => v !== 128)) {
        drawAudioBars(ctx, data, "#fff", true);
      } else {
        const pattern = generateStaticPattern();
        drawAudioBars(ctx, pattern, "#909090", true);
      }
    }
  }, [isPlaying, isPaused]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
      style={{ backgroundColor: 'transparent', display: 'block' }}
    />
  );
};